import { useCallback, useEffect, useRef, useState } from "react";
import { callFirebaseFunction } from "./firebaseio/firebaseFunctions";
import { RyeButton } from "./rye/RyeButton";
import { buildClasses } from "./utils/buildClasses";
import { StripeEmbeddedCheckout } from "./StripeEmbeddedCheckout";
import { getAuth, signOut } from "firebase/auth";
import { RyeSkeleton } from "./rye/RyeSkeleton";

interface SubscriptionRequiredProps {
  subscriptionId: string | null;
  subscriptionStatus: string | null;
}

export function SubscriptionRequired(
  props: SubscriptionRequiredProps,
): JSX.Element {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const checkoutRequestInFlight = useRef(false);

  const shouldShowCheckout = props.subscriptionId == null;

  const handleStartCheckout = useCallback(async () => {
    // Prevent duplicate sessions when StrictMode repeats the initial effect.
    if (
      !shouldShowCheckout ||
      clientSecret != null ||
      checkoutRequestInFlight.current
    ) {
      return;
    }

    checkoutRequestInFlight.current = true;
    setError(null);

    try {
      const result = await callFirebaseFunction("StripeCreateSession", {});
      const data = result.data;

      if (data.success && data.clientSecret && data.sessionId) {
        setClientSecret(data.clientSecret);
        setCheckoutSessionId(data.sessionId);
      } else {
        setError(data.error_msg || "Failed to create checkout session");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError("Failed to start checkout. Please try again.");
    } finally {
      checkoutRequestInFlight.current = false;
    }
  }, [shouldShowCheckout, clientSecret]);

  useEffect(() => {
    void handleStartCheckout();
  }, [handleStartCheckout]);

  const handleCheckoutComplete = async (): Promise<void> => {
    if (!checkoutSessionId) {
      setError(
        "Checkout completed, but we could not verify your subscription. Please email us.",
      );
      return;
    }

    try {
      const result = await callFirebaseFunction(
        "StripeCheckoutSessionCompleted",
        { sessionId: checkoutSessionId },
      );
      const data = result.data;

      if (!data.success) {
        setError(
          data.error_msg ||
            "Payment completed, but we failed to finalize checkout. Please email us.",
        );
        return;
      }

      // Reload the page to re-check subscription status
      window.location.reload();
    } catch (err) {
      console.error("Failed to finalize Stripe checkout:", err);
      setError("Failed to finalize checkout. Please refresh the page.");
    }
  };

  const handleSignOut = async () => {
    const auth = getAuth();
    await signOut(auth);
  };

  return (
    <div
      className={buildClasses(
        "min-h-screen",
        "bg-surface-50",
        "flex",
        "flex-col",
        "items-center",
        "p-4",
      )}
    >
      <div
        className={buildClasses(
          "flex-1",
          "flex",
          "items-center",
          "justify-center",
          "w-full",
        )}
      >
        <div
          className={buildClasses(
            "max-w-2xl",
            "w-full",
            "bg-white",
            "rounded-lg",
            "shadow-lg",
            "p-4",
            "sm:p-6",
          )}
        >
          {!shouldShowCheckout ? (
            <p>
              You have an existing subscription, but it is{" "}
              {props.subscriptionStatus}. Please contact us for assistance.
            </p>
          ) : clientSecret != null ? (
            <StripeEmbeddedCheckout
              clientSecret={clientSecret}
              onComplete={handleCheckoutComplete}
            />
          ) : error == null ? (
            <RyeSkeleton shape="text" />
          ) : (
            <>
              <h1
                className={buildClasses(
                  "text-2xl",
                  "font-bold",
                  "text-on-surface-900",
                  "mb-4",
                )}
              >
                Unable to Load Checkout
              </h1>
              <div
                className={buildClasses(
                  "p-4",
                  "bg-red-50",
                  "border",
                  "border-red-200",
                  "rounded-md",
                  "text-red-800",
                  "text-sm",
                  "mb-4",
                )}
              >
                {error}
              </div>
              <RyeButton
                text="Try Again"
                onClick={handleStartCheckout}
                icon="refresh"
                vibe="primary"
                variant="filled"
              />
            </>
          )}
        </div>
      </div>
      <div className={buildClasses("mt-4")}>
        <RyeButton
          text="Sign Out"
          onClick={handleSignOut}
          vibe="subdued"
          variant="transparent"
          size="sm"
        />
      </div>
    </div>
  );
}
